/* eslint-disable react/prop-types */
"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/axios";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { buildAuthRequiredPath } from "../../utils/authRedirect";
import { prepareEventCheckoutUrl } from "../../utils/eventCheckout";
import { isNativeApp, openExternalUrl } from "../../native/runtime";
import { LoadingSkeleton } from "../common/LoadingSkeleton";

const HOST_PAID_CANCELLATION_RULE_KEYS = ["rule_1", "rule_2", "rule_3"];
const PAYMENT_QR_BEFORE_MS = 60 * 60 * 1000;

const getNormalizedCancellationPolicy = (rawValue) => {
  const normalized = String(rawValue || "").trim().toLowerCase();
  if (normalized === "flexible" || normalized === "moderate" || normalized === "strict") {
    return normalized;
  }
  return "moderate";
};

const getCancellationPolicyContent = (t, rawPolicy) => {
  const policy = getNormalizedCancellationPolicy(rawPolicy);
  const root = `event_create.step3.payment.host_paid.cancellation_options.${policy}`;
  const rules = HOST_PAID_CANCELLATION_RULE_KEYS.map((ruleKey) => {
    const conditionKey = `${root}.${ruleKey}_condition`;
    const outcomeKey = `${root}.${ruleKey}_outcome`;
    const condition = t(conditionKey);
    const outcome = t(outcomeKey);
    if (condition === conditionKey) return null;
    return {
      key: ruleKey,
      condition,
      outcome: outcome === outcomeKey ? "" : outcome,
    };
  }).filter(Boolean);

  return {
    label: t(`${root}.label`),
    rules,
  };
};

const getOrganizerBusinessTypeLabel = (t, rawType, isBusinessHost = false) => {
  const normalized = String(rawType || "").trim().toLowerCase();
  if (normalized === "company") {
    return t("confirm_intent_modal.body.organizer_type_company");
  }
  if (normalized === "individual") {
    return t("confirm_intent_modal.body.organizer_type_individual");
  }
  if (isBusinessHost) {
    return t("confirm_intent_modal.body.organizer_type_company");
  }
  return "";
};

const HostPaidCancellationNotice = ({ t, policy, hasProcessingFee }) => {
  const content = getCancellationPolicyContent(t, policy);

  return (
    <div className="rounded-2xl border border-white/12 bg-white/[0.04] px-4 py-4 text-left">
      <div className="text-sm font-semibold uppercase tracking-[0.16em] text-white/55">
        {t("confirm_intent_modal.body.cancellation_policy_title")}
      </div>
      <div className="mt-3 text-base font-semibold text-white">
        {content.label}
      </div>
      <div className="mt-2 space-y-2">
        {content.rules.map((rule) => (
          <div key={rule.key} className="flex items-start justify-between gap-3 text-sm leading-6">
            <span className="text-white/90">{rule.condition}</span>
            {rule.outcome ? (
              <span className="shrink-0 font-medium text-white/72">{rule.outcome}</span>
            ) : null}
          </div>
        ))}
      </div>
      {hasProcessingFee ? (
        <p className="mt-3 text-sm leading-6 text-white/62">
          {t("confirm_intent_modal.body.cancellation_policy_service_fee_note")}
        </p>
      ) : null}
      <p className="mt-2 text-xs leading-5 text-white/62">
        {t("confirm_intent_modal.body.host_change_refund_note")}
      </p>
    </div>
  );
};

const resolveLegalLanguageCode = (i18n) => {
  const raw = String(i18n?.resolvedLanguage || i18n?.language || "en").toLowerCase();
  if (raw.startsWith("ru")) return "ru";
  if (raw.startsWith("sk")) return "sk";
  if (raw.startsWith("cs")) return "sk";
  return "en";
};

const resolveDateLocaleCode = (i18n) => {
  if (String(i18n?.resolvedLanguage || i18n?.language || "").toLowerCase().startsWith("cs")) return "cs-CZ";
  const language = resolveLegalLanguageCode(i18n);
  if (language === "sk") return "sk-SK";
  if (language === "ru") return "ru-RU";
  return "en-US";
};

const buildPaidParticipantTermsHref = (language) => {
  if (language === "ru" || language === "sk" || language === "en") {
    return `/paid-ticket-participant-terms/${language}`;
  }
  return "/paid-ticket-participant-terms";
};

const HostPaidOrganizerCard = ({
  t,
  organizerName,
  organizerType = "",
}) => (
  <div className="rounded-2xl border border-cyan-400/18 bg-cyan-400/[0.08] px-4 py-4 text-left shadow-[0_18px_36px_rgba(8,145,178,0.14)]">
    <div className="space-y-2.5">
      <div className="flex items-start justify-between gap-4 text-sm leading-6">
        <span className="text-white/58">{t("confirm_intent_modal.body.organizer_label")}</span>
        <span className="text-right font-medium text-white">{organizerName || "Organizer"}</span>
      </div>
      {organizerType ? (
        <div className="flex items-start justify-between gap-4 text-sm leading-6">
          <span className="text-white/58">{t("confirm_intent_modal.body.organizer_type_label")}</span>
          <span className="text-right font-medium text-white">{organizerType}</span>
        </div>
      ) : null}
    </div>
  </div>
);

/**
 * ConfirmIntentModal
 *
 * A lightweight modal that asks the user to confirm their intention to attend an event.
 * No map / QR, just two actions: Confirm intention (Yes) or Decline (No, plans changed).
 */
const ConfirmIntentModal = ({
  visible = false,
  eventId,
  eventType,
  onClose,
  onConfirmSuccess,
  onDeclineSuccess,
  // New: multi-mode modal. 'intent' (default), 'payment', 'activation_date', or 'slot'
  variant = "intent",
  // New: optional props to bypass extra GET and drive the UI directly
  payUrl,
  price: priceProp,
  paid: paidProp,
  isPayableInAdvance: isPayableProp,
  needImmediatePayment: needImmediateProp,
  paymentContext = null,
  preJoinPayment = false,
  ticketMode = false,
  onShowQr,
  onGenderRequired,
  onCheckoutCapacityReserved,
}) => {
  const { t, i18n } = useTranslation("events");
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitState, setSubmitState] = useState("idle"); // idle | pending | success | error
  const [eventData, setEventData] = useState(null); // { id, name, date_time, intent_status, is_host, is_participant }
  const [redirecting, setRedirecting] = useState(false);
  const [manualPaymentReminder, setManualPaymentReminder] = useState(ticketMode);
  const [selectedMultisport, setSelectedMultisport] = useState(null);
  const [selectedTicketCategory, setSelectedTicketCategory] = useState(null);
  const [hostPaidParticipantTermsAccepted, setHostPaidParticipantTermsAccepted] = useState(false);
  const [declineConfirmArmed, setDeclineConfirmArmed] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // --- helper: если 403 из-за отсутствия авторизации → редирект на /auth
  const maybeRedirectToAuth = useCallback((err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    // Наши бизнес-ошибки 403 возвращают поле "error", а не "detail".
    if (status === 403 && detail) {
      try {
        const currentPath =
          (location?.pathname || "") +
          (location?.search || "") +
          (location?.hash || "");
        navigate(buildAuthRequiredPath({ next: currentPath || "/events/search" }), { replace: true });
      } catch {
        // Fallback, если что-то пошло не так при формировании next
        navigate(buildAuthRequiredPath(), { replace: true });
      }
      return true;
    }
    return false;
  }, [location?.hash, location?.pathname, location?.search, navigate]);

  // Keep local flag in sync with prop
  useEffect(() => {
    setManualPaymentReminder(ticketMode);
  }, [ticketMode]);

  // Detect manual payment reminder flag in query string on open (deep links)
  useEffect(() => {
    if (!visible) return;
    if (typeof window === "undefined") return;
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const flag = sp.get("manual_pay") || sp.get("manual_payment");
      if (flag === "1" || flag === "true") {
        setManualPaymentReminder(true);
      }
    } catch {
      // ignore
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    setHostPaidParticipantTermsAccepted(false);
    setDeclineConfirmArmed(false);
  }, [visible, eventId, variant, preJoinPayment]);

  // Helpers to normalize backend flags/fields
  const isPayableInAdvance = (data) => {
    if (!data) return false;
    return (
      data.is_payable_in_advance === true ||
      data.pay_in_advance === true ||
      data.requires_prepayment === true
    );
  };

  const isAlreadyPaid = (data) => {
    if (!data) return false;
    // support multiple possible field names
    return (
      data.paid === true ||
      data.is_paid === true ||
      data.ticket_paid === true
    );
  };

  const getTicketPrice = (data) => {
    if (!data) return null;
    const p = data.ticket_price ?? data.price ?? data.ticket_amount;
    // keep only sensible numeric
    const num =
      typeof p === "string"
        ? parseFloat(p)
        : typeof p === "number"
        ? p
        : null;
    return Number.isFinite(num) ? num : null;
  };

  const appendMultisportChoice = (url, choice) => {
    if (!url || (choice !== true && choice !== false)) return url;
    try {
      const resolved = new URL(resolveCheckoutUrl(url) || url, window.location.origin);
      resolved.searchParams.set("use_multisport", choice ? "1" : "0");
      return resolved.toString();
    } catch {
      const glue = String(url).includes("?") ? "&" : "?";
      return `${url}${glue}use_multisport=${choice ? "1" : "0"}`;
    }
  };

  const appendTicketCategory = (url, categoryId) => {
    if (!url || !categoryId) return url;
    try {
      const resolved = new URL(resolveCheckoutUrl(url) || url, window.location.origin);
      resolved.searchParams.set("ticket_category", categoryId);
      return resolved.toString();
    } catch {
      const glue = String(url).includes("?") ? "&" : "?";
      return `${url}${glue}ticket_category=${encodeURIComponent(categoryId)}`;
    }
  };

  const applyPaymentStepToUrl = (url, paymentStep) => {
    if (!url || !paymentStep) return url;
    try {
      const resolved = new URL(resolveCheckoutUrl(url) || url, window.location.origin);
      resolved.searchParams.set("step", paymentStep);
      return resolved.toString();
    } catch {
      const glue = String(url).includes("?") ? "&" : "?";
      return `${url}${glue}step=${encodeURIComponent(paymentStep)}`;
    }
  };

  const applyConfirmedGenderToUrl = (url, confirmedGender) => {
    if (!url || !confirmedGender) return url;
    try {
      const resolved = new URL(resolveCheckoutUrl(url) || url, window.location.origin);
      resolved.searchParams.set("confirmed_gender", confirmedGender);
      return resolved.toString();
    } catch {
      const glue = String(url).includes("?") ? "&" : "?";
      return `${url}${glue}confirmed_gender=${encodeURIComponent(confirmedGender)}`;
    }
  };

  // Normalize checkout URL coming from the backend or props.
  const resolveCheckoutUrl = (u) => {
    if (!u) return null;

    let origin = typeof window !== "undefined" ? window.location.origin : "";

    if (/^https?:\/\//i.test(u)) {
      try {
        const url = new URL(u);
        if (!origin || url.origin !== origin) {
          return u;
        }
        u = `${url.pathname}${url.search || ""}`;
      } catch {
        return u;
      }
    }

    const base = api?.defaults?.baseURL || "";
    let basePath = "";

    try {
      if (base) {
        const b = new URL(base, origin || undefined);
        origin = b.origin;
        basePath = b.pathname.replace(/\/$/, "");
      }
    } catch {
      // ignore
    }

    const path = u.startsWith("/") ? u : `/${u}`;

    if (path.startsWith("/api/")) {
      return `${origin}${path}`;
    }

    return `${origin}${basePath}${path}`;
  };

  const formatEventDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return new Intl.DateTimeFormat(resolveDateLocaleCode(i18n), {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    } catch {
      return iso;
    }
  };

  // In Telegram/Instagram/Facebook in-app browsers, `window.open` may silently fail.
  // Use same-tab navigation there so the checkout always starts.
  const isLikelyInAppBrowser = () => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator?.userAgent || "";
    const hasTelegramWebApp = Boolean(window.Telegram?.WebApp?.initData);
    return (
      hasTelegramWebApp ||
      /Instagram|FBAN|FBAV|Telegram/i.test(ua)
    );
  };

  // Fetch minimal info and current intent status
  useEffect(() => {
    if (!visible || !eventId) return;

    // Pre-join payment flow: user is not a participant yet, and the intent endpoint can return
    // a business error like "You are not registered for this event." which we don't want to show.
    // Also, we don't need intent status for this UI.
    if (variant === "payment" && preJoinPayment) {
      setErrorMsg("");
      return;
    }

    // In 'payment' mode, only skip GET if paidProp is explicitly provided.
    if (variant === "payment" && typeof paidProp === "boolean") {
      return;
    }

    let mounted = true;
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const endpoint =
          variant === "activation_date"
            ? `/events/activation-date/confirm/${eventId}/`
            : variant === "slot"
            ? `/events/slot/confirm/${eventId}/`
            : `/events/intent/confirm/${eventId}/`;
        const { data } = await api.get(endpoint);
        if (mounted) {
          setEventData(data);
          const alreadyConfirmed =
            (variant === "activation_date" && data?.activation_date_status === "confirmed")
            || (variant === "slot" && data?.slot_confirm_status === "confirmed");
          if (alreadyConfirmed) {
            onCloseRef.current?.();
          }
        }
      } catch (err) {
        // если это "Authentication credentials were not provided." → уводим на /auth
        if (maybeRedirectToAuth(err)) return;

        const msg =
          err?.response?.data?.error ||
          err?.message ||
          t("confirm_intent_modal.errors.load_failed");
        if (mounted) setErrorMsg(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [
    visible,
    eventId,
    variant,
    preJoinPayment,
    payUrl,
    paidProp,
    isPayableProp,
    needImmediateProp,
    priceProp,
    maybeRedirectToAuth,
    t,
  ]);

  useEffect(() => {
    if (!visible) return undefined;
    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [visible]);

  const multisportPricing =
    paymentContext?.multisportPricing ||
    eventData?.ticket_payment_state?.multisport_pricing ||
    eventData?.multisport_pricing ||
    null;
  const multisportChoiceRequired =
    Boolean(multisportPricing?.enabled) &&
    selectedMultisport !== true &&
    selectedMultisport !== false;
  const ticketCategoriesState = useMemo(
    () =>
      paymentContext?.ticketCategories ||
      eventData?.ticket_payment_state?.ticket_categories ||
      (Array.isArray(eventData?.ticket_categories) && eventData.ticket_categories.length
        ? { enabled: true, options: eventData.ticket_categories }
        : null),
    [
      eventData?.ticket_categories,
      eventData?.ticket_payment_state?.ticket_categories,
      paymentContext?.ticketCategories,
    ]
  );
  const ticketCategoryOptions = Array.isArray(ticketCategoriesState?.options)
    ? ticketCategoriesState.options
    : [];
  const ticketCategoryChoiceRequired =
    Boolean(ticketCategoriesState?.enabled) && !selectedTicketCategory;

  useEffect(() => {
    if (!visible) return;
    if (!multisportPricing?.enabled) {
      setSelectedMultisport(null);
      return;
    }
    setSelectedMultisport(
      multisportPricing.selected === true
        ? true
        : multisportPricing.selected === false
          ? false
          : null
    );
  }, [multisportPricing, visible]);

  useEffect(() => {
    if (!visible) return;
    if (!ticketCategoriesState?.enabled) {
      setSelectedTicketCategory(null);
      return;
    }
    setSelectedTicketCategory(ticketCategoriesState.selected || null);
  }, [ticketCategoriesState, visible]);

  const requiresDeclineClickConfirmation =
    !preJoinPayment &&
    (
      variant === "intent" ||
      variant === "payment" ||
      variant === "activation_date" ||
      variant === "slot"
    );

  const handleConfirm = async () => {
    if (!eventId || submitState === "pending") return;
    setDeclineConfirmArmed(false);
    setSubmitState("pending");
    setErrorMsg("");
    try {
      const endpoint =
        variant === "activation_date"
          ? `/events/activation-date/confirm/${eventId}/`
          : variant === "slot"
          ? `/events/slot/confirm/${eventId}/`
          : `/events/intent/confirm/${eventId}/`;
      const { data } = await api.post(endpoint);
      setSubmitState("success");
      setEventData((prev) => {
        if (!prev) return prev;
        if (variant === "activation_date") {
          return {
            ...prev,
            activation_date_status: "confirmed",
            intent_status: data?.intent_status || prev.intent_status,
            activation_date_counts_as_intent:
              typeof data?.activation_date_counts_as_intent === "boolean"
                ? data.activation_date_counts_as_intent
                : prev.activation_date_counts_as_intent,
          };
        }
        if (variant === "slot") {
          return { ...prev, slot_confirm_status: "confirmed" };
        }
        return { ...prev, intent_status: "confirmed" };
      });
      const msg =
        variant === "activation_date"
          ? (
              data?.activation_date_counts_as_intent
                ? t("confirm_intent_modal.toasts.activation_date_intent_confirmed")
                : t(
                    isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.toasts.activation_time_confirmed"
                      : "confirm_intent_modal.toasts.activation_date_confirmed"
                  )
            )
          : t("confirm_intent_modal.toasts.intention_confirmed");
      window.dispatchEvent(
        new CustomEvent("global-info", { detail: { message: msg } })
      );
      onConfirmSuccess?.(variant, data);
      setTimeout(() => {
        setSubmitState("idle");
        onClose?.();
      }, 900);
    } catch (err) {
      if (maybeRedirectToAuth(err)) return;

      const msg =
        err?.response?.data?.error ||
        err?.message ||
        (variant === "activation_date"
          ? t("confirm_intent_modal.errors.activation_date_confirm_failed")
          : t("confirm_intent_modal.errors.confirm_failed"));
      setErrorMsg(msg);
      setSubmitState("error");
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg } })
      );
    }
  };

  const handleDecline = async () => {
    if (!eventId || submitState === "pending") return;
    if (requiresDeclineClickConfirmation && !declineConfirmArmed) {
      setDeclineConfirmArmed(true);
      setErrorMsg("");
      return;
    }

    setSubmitState("pending");
    setErrorMsg("");
    try {
      const endpoint =
        variant === "activation_date"
          ? `/events/activation-date/decline/${eventId}/`
          : variant === "slot"
          ? `/events/slot/decline/${eventId}/`
          : `/events/intent/decline/${eventId}/`;
      await api.post(endpoint);
      setSubmitState("success");
      const msg =
        variant === "activation_date"
          ? t(
              eventData?.paid_reschedule_refund_available
                ? (
                    isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.toasts.activation_time_refund_requested"
                      : "confirm_intent_modal.toasts.activation_date_refund_requested"
                  )
                : (
                    isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.toasts.activation_time_removed"
                      : "confirm_intent_modal.toasts.activation_date_removed"
                  )
            )
          : t("confirm_intent_modal.toasts.removed_from_event");
      window.dispatchEvent(
        new CustomEvent("global-info", { detail: { message: msg } })
      );
      onDeclineSuccess?.(variant);
      setTimeout(() => {
        setSubmitState("idle");
        onClose?.();
      }, 900);
    } catch (err) {
      if (maybeRedirectToAuth(err)) return;

      const msg =
        err?.response?.data?.error ||
        err?.message ||
        (variant === "activation_date"
          ? t("confirm_intent_modal.errors.activation_date_action_failed")
          : t("confirm_intent_modal.errors.action_failed"));
      setErrorMsg(msg);
      setSubmitState("error");
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg } })
      );
    }
  };

  const handleCancel = () => {
    if (redirecting) return;
    setDeclineConfirmArmed(false);
    onClose?.();
  };

  const openCheckoutWindow = (url) => {
    if (typeof window === "undefined") return false;

    const w = window.open("", "_blank");
    if (!w) return false;

    const escapeLoaderHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const loaderTitle = escapeLoaderHtml(t("confirm_intent_modal.loader.title"));
    const loaderSubtitle = escapeLoaderHtml(t("confirm_intent_modal.loader.subtitle"));
    const loaderNote = escapeLoaderHtml(t("confirm_intent_modal.loader.note"));

    try {
      w.document.open();
      w.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
  <meta name="theme-color" content="#050812" />
  <title>${loaderTitle}</title>
  <style>
    :root {
      color-scheme: dark;
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      background: #050812;
      --vg-platform-background: url("data:image/svg+xml;utf8,<svg viewBox='0 0 402 874' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%25' width='100%25' fill='url(%23grad)' opacity='1'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(-0.0000031291 -87.4 104.99 -0.000003759 201 874)'><stop stop-color='rgba(32,32,32,1)' offset='0'/><stop stop-color='rgba(18,20,25,1)' offset='0.1226'/><stop stop-color='rgba(5,8,18,1)' offset='0.24519'/><stop stop-color='rgba(12,21,47,1)' offset='0.3149'/><stop stop-color='rgba(19,34,77,1)' offset='0.38462'/><stop stop-color='rgba(26,47,106,1)' offset='0.45433'/><stop stop-color='rgba(33,60,135,1)' offset='0.52404'/><stop stop-color='rgba(48,85,193,1)' offset='0.66346'/><stop stop-color='rgba(62,111,251,1)' offset='0.80288'/><stop stop-color='rgba(96,137,253,1)' offset='0.90144'/><stop stop-color='rgba(130,162,255,1)' offset='1'/></radialGradient></defs></svg>");
    }
    * {
      box-sizing: border-box;
    }
    html,
    body {
      min-height: 100%;
      background: #050812;
    }
    body {
      margin: 0;
      min-height: 100vh;
      min-height: 100svh;
      color: rgba(255,255,255,0.94);
      background-color: #050812;
      background-image: var(--vg-platform-background);
      background-position: center top;
      background-repeat: no-repeat;
      background-size: 100% 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: max(24px, env(safe-area-inset-top)) max(18px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(18px, env(safe-area-inset-left));
    }
    body::before {
      content: "";
      position: fixed;
      inset: 0;
      pointer-events: none;
      background: rgba(2,6,20,0.42);
    }
    .shell {
      position: relative;
      width: min(520px, 100%);
      overflow: hidden;
      border-radius: 30px 10px 30px 10px;
      border: 1px solid rgba(255,255,255,0.58);
      background: linear-gradient(105deg, rgba(42,45,56,0.78) 0%, rgba(26,34,48,0.72) 48%, rgba(35,48,66,0.78) 100%);
      box-shadow:
        0 24px 80px rgba(0,0,0,0.36),
        inset 0 1px 0 rgba(255,255,255,0.10);
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
      padding: 34px 30px 30px;
      text-align: center;
    }
    .skeleton {
      position: relative;
      width: min(220px, 72%);
      height: 14px;
      margin: 0 auto 22px;
      border-radius: 999px;
      overflow: hidden;
      background: rgba(255,255,255,0.18);
    }
    .skeleton::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.34), transparent);
      transform: translateX(-100%);
      animation: shimmer 1.8s ease-in-out infinite;
    }
    @keyframes shimmer {
      to { transform: translateX(100%); }
    }
    .title {
      margin: 0 auto;
      max-width: 410px;
      font-size: clamp(28px, 6.2vw, 32px);
      line-height: 1.22;
      font-weight: 700;
      letter-spacing: 0;
    }
    .subtitle {
      max-width: 410px;
      margin: 18px auto 0;
      font-size: 15px;
      line-height: 1.7;
      font-weight: 500;
      color: rgba(255,255,255,0.84);
    }
    .note {
      margin: 18px auto 0;
      max-width: 390px;
      font-size: 13px;
      line-height: 1.55;
      font-weight: 500;
      color: rgba(255,255,255,0.58);
    }
    @media (max-width: 420px) {
      .shell {
        border-radius: 28px 10px 28px 10px;
        padding: 30px 22px 26px;
      }
    }
  </style>
</head>
<body>
  <main class="shell" aria-live="polite" aria-busy="true">
    <div class="skeleton" aria-hidden="true"></div>
    <h1 class="title">${loaderTitle}</h1>
    <p class="subtitle">${loaderSubtitle}</p>
    <p class="note">${loaderNote}</p>
  </main>
</body>
</html>`);
      w.document.close();
    } catch {
      try {
        w.location.href = url;
      } catch {
        // ignore
      }
      return true;
    }

    try {
      w.opener = null;
    } catch {
      // ignore
    }

    window.setTimeout(() => {
      try {
        w.location.href = url;
      } catch {
        try {
          w.location = url;
        } catch {
          // ignore
        }
      }
    }, 450);

    return true;
  };

  const handlePay = async (requestedPaymentStep = null, confirmedGender = null) => {
    if (!eventId || submitState === "pending" || redirecting) return;
    if (multisportChoiceRequired) {
      setErrorMsg(t("confirm_intent_modal.multisport.choice_required"));
      return;
    }
    if (ticketCategoryChoiceRequired) {
      setErrorMsg(t("confirm_intent_modal.ticket_categories.choice_required"));
      return;
    }
    setDeclineConfirmArmed(false);
    setSubmitState("pending");
    setRedirecting(true);
    setErrorMsg("");

    try {
      const candidate =
        (requestedPaymentStep === "join_full" &&
        typeof paymentContext?.payFullNowUrl === "string" &&
        paymentContext.payFullNowUrl
          ? paymentContext.payFullNowUrl
          : null) ||
        (typeof payUrl === "string" && payUrl) ||
        (eventData?.pay_url ? eventData.pay_url : null) ||
        (eventData?.checkout_url ? eventData.checkout_url : null) ||
        (eventData?.shopify_url ? eventData.shopify_url : null);

      let directUrl = resolveCheckoutUrl(candidate);

      if (!directUrl) {
        const base = (api?.defaults?.baseURL || "").replace(/\/$/, "");
        if (base) directUrl = `${base}/events/${eventId}/buy/`;
      }

      const multisportState = multisportPricing;
      if (!directUrl) {
        setSubmitState("error");
        setRedirecting(false);
        const msg = t("confirm_intent_modal.errors.no_checkout_url");
        setErrorMsg(msg);
        console.warn("[ConfirmIntentModal] No checkout URL", {
          eventId,
          candidate,
          eventData,
        });
        return;
      }

      // Ensure an absolute URL before navigation (important for in-app redirects).
      directUrl = resolveCheckoutUrl(directUrl) || directUrl;
      if (requestedPaymentStep) {
        directUrl = applyPaymentStepToUrl(directUrl, requestedPaymentStep);
      }
      if (confirmedGender) {
        directUrl = applyConfirmedGenderToUrl(directUrl, confirmedGender);
      }
      if (multisportState?.enabled) {
        directUrl = appendMultisportChoice(directUrl, selectedMultisport);
      }
      if (ticketCategoriesState?.enabled) {
        directUrl = appendTicketCategory(directUrl, selectedTicketCategory);
      }

      // Never navigate Safari or an in-app browser directly to the protected
      // VaultGrid API endpoint. Resolve it while the app can still attach the
      // access token, then open only the public provider checkout URL.
      directUrl = await prepareEventCheckoutUrl({ directUrl, eventId });

      try {
        window.sessionStorage?.setItem(
          "vg_checkout_return_guard",
          JSON.stringify({
            eventId: String(eventId),
            startedAt: Date.now(),
          })
        );
      } catch {
        // ignore sessionStorage failures
      }

      if (isNativeApp()) {
        await openExternalUrl(directUrl);
        setSubmitState("idle");
        setTimeout(() => setRedirecting(false), 400);
        return;
      }

      // In-app browsers are unreliable with popups; force same-tab redirect.
      if (isLikelyInAppBrowser()) {
        try {
          window.location.assign(directUrl);
          setSubmitState("idle");
          setTimeout(() => setRedirecting(false), 400);
          return;
        } catch (innerErr) {
          console.warn(
            "[ConfirmIntentModal] In-app same-tab redirect failed",
            innerErr
          );
        }
      }

      const opened = openCheckoutWindow(directUrl);

      // Если попап заблокирован (window.open вернул null) — пробуем обычный редирект в текущем окне.
      if (!opened) {
        try {
          if (typeof window !== "undefined") {
            window.location.href = directUrl;
            // Оставляем состояние "idle", чтобы кнопка не зависала в pending.
            setSubmitState("idle");
            setTimeout(() => setRedirecting(false), 400);
            return;
          }
        } catch (innerErr) {
          console.warn(
            "[ConfirmIntentModal] Fallback redirect failed",
            innerErr
          );
        }

        // Если даже прямой редирект не удался — только тогда показываем ошибку.
        const msg = t("confirm_intent_modal.errors.payment_start_failed");
        setErrorMsg(msg);
        setSubmitState("error");
        setRedirecting(false);
        return;
      }

      // Ветка успеха: checkout открыт в новой вкладке, возвращаем UI в нормальное состояние.
      setSubmitState("idle");
      setTimeout(() => setRedirecting(false), 400);
    } catch (err) {
      const code = String(err?.response?.data?.code || "").trim();
      if (
        [
          "checkout_capacity_reserved",
          "gender_segment_full",
          "seat_hold_no_capacity",
        ].includes(code) &&
        typeof onCheckoutCapacityReserved === "function"
      ) {
        setSubmitState("idle");
        setRedirecting(false);
        setErrorMsg("");
        onCheckoutCapacityReserved(err?.response?.data || null);
        return;
      }
      if (code === "gender_confirmation_required" && typeof onGenderRequired === "function") {
        setSubmitState("idle");
        setRedirecting(false);
        setErrorMsg("");
        onGenderRequired({
          resume: (gender) => handlePay(requestedPaymentStep, gender),
        });
        return;
      }
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("confirm_intent_modal.errors.payment_start_failed");
      setErrorMsg(msg);
      setSubmitState("error");
      setRedirecting(false);
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg } })
      );
    }
  };

  const isActivationDateVariant = variant === "activation_date";
  const activationDateCountsAsIntent = Boolean(
    isActivationDateVariant && eventData?.activation_date_counts_as_intent
  );
  const isTimeOnlyScheduleChange = Boolean(
    isActivationDateVariant && eventData?.last_schedule_change_kind === "time"
  );
  const paidRescheduleRefundAvailable = Boolean(
    isActivationDateVariant && eventData?.paid_reschedule_refund_available
  );

  const payable =
    typeof isPayableProp === "boolean"
      ? isPayableProp
      : variant === "payment"
      ? true
      : isPayableInAdvance(eventData);

  const alreadyPaid =
    typeof paidProp === "boolean" ? paidProp : isAlreadyPaid(eventData);

  const showAlreadyPaid = alreadyPaid && variant === "payment";

  const price =
    typeof priceProp !== "undefined" && priceProp !== null
      ? typeof priceProp === "string"
        ? parseFloat(priceProp)
        : priceProp
      : getTicketPrice(eventData);
  const formatMoney = (value) => {
    const num =
      typeof value === "string"
        ? parseFloat(value)
        : typeof value === "number"
        ? value
        : null;
    return Number.isFinite(num) ? num.toFixed(2) : "0.00";
  };
  const paymentCurrency = String(
    paymentContext?.currency || eventData?.currency || "EUR"
  ).toUpperCase();
  const paymentCurrencySymbol = paymentCurrency === "RUB" ? "₽" : "€";
  const paymentActionLabel = (key, amount) =>
    t(key, { price: amount }).replaceAll("€", paymentCurrencySymbol);

  const depositPercent = Number(paymentContext?.depositPercent);
  const remainingPercent = Number(paymentContext?.remainingPercent);
  const balanceDueDaysBefore = Number(paymentContext?.balanceDueDaysBefore);
  const balanceDueDate = paymentContext?.balanceDueAt ? new Date(paymentContext.balanceDueAt) : null;
  const balanceDueLabel = balanceDueDate && !Number.isNaN(balanceDueDate.getTime())
    ? balanceDueDate.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
  const paymentStep = String(
    paymentContext?.paymentStep || eventData?.payment_step || ""
  ).trim().toLowerCase();
  const allowFullPaymentOnJoin = Boolean(
    paymentContext?.allowFullPaymentOnJoin ??
      paymentContext?.allow_full_payment_on_join ??
      eventData?.host_paid_allow_full_ticket_payment_on_join
  );
  const selectedTicketCategoryOption = ticketCategoryOptions.find(
    (item) => String(item?.id || "") === String(selectedTicketCategory || "")
  );
  const effectiveFullTicketPrice =
    selectedTicketCategoryOption
      ? Number(selectedTicketCategoryOption.price || 0)
      : multisportPricing?.enabled && selectedMultisport === true
      ? Number(multisportPricing?.multisport_ticket_price || 0)
      : multisportPricing?.enabled && selectedMultisport === false
        ? Number(multisportPricing?.regular_ticket_price || 0)
        : Number(paymentContext?.fullTicketPrice ?? price ?? 0);
  const selectedMultisportCheckoutPrice = Number(
    selectedMultisport === true
      ? multisportPricing?.multisport_ticket_price
      : selectedMultisport === false
        ? multisportPricing?.regular_ticket_price
        : NaN
  );
  const explicitDepositAmount = Number(paymentContext?.depositAmount);
  const explicitDepositCheckoutAmount = Number(
    paymentContext?.depositCheckoutAmount ?? paymentContext?.deposit_checkout_amount
  );
  const explicitRemainingAmount = Number(paymentContext?.remainingAmount);
  const explicitFullCheckoutAmount = Number(
    paymentContext?.fullCheckoutAmount ?? paymentContext?.full_checkout_amount
  );
  const explicitNextCheckoutAmount = Number(
    paymentContext?.nextCheckoutAmount ?? paymentContext?.next_checkout_amount
  );
  const explicitNextTicketBaseAmount = Number(
    paymentContext?.nextTicketBaseAmount ?? paymentContext?.next_ticket_base_amount
  );
  const explicitFullParticipantServiceFee = Number(
    paymentContext?.fullParticipantServiceFee ??
      paymentContext?.full_participant_service_fee
  );
  const fallbackDepositAmount =
    selectedTicketCategoryOption && Number(selectedTicketCategoryOption.price) > 0
      ? Number(
          (
            Number(selectedTicketCategoryOption.price) *
            (Math.max(0, Math.min(100, Number(depositPercent || 100))) / 100)
          ).toFixed(2)
        )
      : Number.isFinite(explicitDepositAmount) && explicitDepositAmount > 0
      ? explicitDepositAmount
      : paymentContext?.isHostPaidCardEvent && Number.isFinite(effectiveFullTicketPrice)
      ? Number(
          (
            effectiveFullTicketPrice *
            (Math.max(0, Math.min(100, Number.isFinite(depositPercent) ? depositPercent : 100)) / 100)
          ).toFixed(2)
        )
      : Number.isFinite(Number(price))
      ? Number(price)
      : Number.isFinite(effectiveFullTicketPrice) && Number.isFinite(depositPercent)
      ? Number(
          (
            effectiveFullTicketPrice *
            (Math.max(0, Math.min(100, Number(depositPercent || 0))) / 100)
          ).toFixed(2)
        )
      : 0;
  const fallbackRemainingAmount =
    Number.isFinite(explicitRemainingAmount) && explicitRemainingAmount >= 0
      ? explicitRemainingAmount
      : Number.isFinite(effectiveFullTicketPrice)
      ? Math.max(0, Number((effectiveFullTicketPrice - fallbackDepositAmount).toFixed(2)))
      : 0;
  const fallbackDepositCheckoutAmount =
    paymentContext?.isHostPaidCardEvent
      ? fallbackDepositAmount
      : Number.isFinite(explicitDepositCheckoutAmount) && explicitDepositCheckoutAmount > 0
      ? explicitDepositCheckoutAmount
      : Number.isFinite(explicitNextCheckoutAmount) && explicitNextCheckoutAmount > 0 && paymentStep !== "remaining_balance"
      ? explicitNextCheckoutAmount
      : fallbackDepositAmount;
  const fallbackFullCheckoutAmount =
    paymentContext?.isHostPaidCardEvent && effectiveFullTicketPrice > 0
      ? effectiveFullTicketPrice
      : Number.isFinite(explicitFullCheckoutAmount) && explicitFullCheckoutAmount > 0
      ? explicitFullCheckoutAmount
      : Number.isFinite(effectiveFullTicketPrice)
      ? Number(
          (
            effectiveFullTicketPrice +
            (Number.isFinite(explicitFullParticipantServiceFee)
              ? explicitFullParticipantServiceFee
              : 0)
          ).toFixed(2)
        )
      : 0;
  const effectiveCheckoutPrice =
    paymentContext?.isHostPaidCardEvent
      ? paymentStep === "remaining_balance"
        ? Number.isFinite(explicitNextTicketBaseAmount) && explicitNextTicketBaseAmount > 0
          ? explicitNextTicketBaseAmount
          : fallbackRemainingAmount
        : fallbackDepositAmount || effectiveFullTicketPrice
      : selectedTicketCategoryOption
      ? Number(selectedTicketCategoryOption.price || 0)
      : Number.isFinite(selectedMultisportCheckoutPrice) &&
    selectedMultisportCheckoutPrice > 0
      ? selectedMultisportCheckoutPrice
      : paymentStep === "remaining_balance" && Number.isFinite(explicitNextCheckoutAmount)
      ? explicitNextCheckoutAmount
      : preJoinPayment && paymentContext?.isHostPaidCardEvent && fallbackDepositAmount > 0
      ? fallbackDepositCheckoutAmount
      : !preJoinPayment && paymentContext?.isHostPaidCardEvent && paymentStep === "remaining_balance" && fallbackRemainingAmount > 0
      ? (Number.isFinite(explicitNextCheckoutAmount) ? explicitNextCheckoutAmount : fallbackRemainingAmount)
      : Number.isFinite(Number(price))
      ? Number(price)
      : paymentContext?.isHostPaidCardEvent && Number.isFinite(Number(paymentContext?.depositPercent))
      ? Number(
          (
            effectiveFullTicketPrice *
            (Math.max(0, Math.min(100, Number(paymentContext?.depositPercent || 0))) / 100)
          ).toFixed(2)
        )
      : effectiveFullTicketPrice;
  const depositAmount = formatMoney(
    paymentContext?.isHostPaidCardEvent ? fallbackDepositAmount || effectiveCheckoutPrice : (price ?? effectiveCheckoutPrice)
  );
  const paymentTicketAmount = formatMoney(
    Number.isFinite(explicitNextTicketBaseAmount) && explicitNextTicketBaseAmount > 0
      ? explicitNextTicketBaseAmount
      : paymentStep === "remaining_balance"
        ? fallbackRemainingAmount
        : fallbackDepositAmount
  );
  const remainingAmount = formatMoney(
    paymentContext?.isHostPaidCardEvent
      ? fallbackRemainingAmount
      : Number(paymentContext?.remainingAmount ?? 0)
  );

  // Block actions if user is not a participant (except in pre-join payment flow).
  const isNotParticipant =
    (eventData && eventData.is_participant === false) ||
    (typeof errorMsg === "string" &&
      /not registered for this event/i.test(errorMsg));

  const blockedByNotParticipant = isNotParticipant && !preJoinPayment;

  const showPaymentUI =
    !alreadyPaid &&
    (variant === "payment" || payable) &&
    !blockedByNotParticipant;
  const paymentQrStartMs = eventData?.date_time
    ? new Date(eventData.date_time).getTime()
    : NaN;
  const paymentQrWindowOpen =
    Number.isFinite(paymentQrStartMs) &&
    nowMs >= paymentQrStartMs - PAYMENT_QR_BEFORE_MS;
  const showQrOption = Boolean(
    showPaymentUI &&
      !preJoinPayment &&
      paymentQrWindowOpen &&
      typeof onShowQr === "function"
  );
  const isPlatformEvent = Boolean(
    eventData?.is_platform_event || paymentContext?.isPlatformEvent
  );

  const hostPaidDepositPayment =
    Boolean(showPaymentUI && preJoinPayment && paymentContext?.isHostPaidCardEvent);
  const hostPaidRemainingPayment = Boolean(
    showPaymentUI &&
    !preJoinPayment &&
    paymentContext?.isHostPaidCardEvent &&
    paymentStep === "remaining_balance"
  );
  const canChooseFullJoinPayment = Boolean(
    hostPaidDepositPayment &&
      allowFullPaymentOnJoin &&
      fallbackDepositAmount > 0 &&
      fallbackRemainingAmount > 0 &&
      fallbackFullCheckoutAmount > fallbackDepositCheckoutAmount
  );
  const hasSplitTicketPayment = Boolean(
    hostPaidDepositPayment &&
      fallbackDepositAmount > 0 &&
      fallbackRemainingAmount > 0 &&
      fallbackDepositAmount < effectiveFullTicketPrice
  );
  const hostPaidCancellationPolicy =
    paymentContext?.cancellationPolicy || eventData?.host_paid_cancellation_policy || null;
  const hasCheckoutProcessingFee = Number(
    paymentContext?.nextServiceFeeAmount ??
      paymentContext?.next_service_fee_amount ??
      paymentContext?.fullParticipantServiceFee ??
      paymentContext?.full_participant_service_fee ??
      0
  ) > 0 && !paymentContext?.isHostPaidCardEvent;
  const language = resolveLegalLanguageCode(i18n);
  const paidParticipantTermsHref = buildPaidParticipantTermsHref(language);
  const organizerName =
    String(
      paymentContext?.organizer?.name ||
      eventData?.host_paid_organizer?.name ||
      eventData?.club?.name ||
      eventData?.business_name ||
      eventData?.host?.code ||
      ""
    ).trim() || t("confirm_intent_modal.body.summary_seller_value");
  const organizerBusinessType = getOrganizerBusinessTypeLabel(
    t,
    paymentContext?.organizer?.business_type ||
      eventData?.host_paid_organizer?.business_type ||
      "",
    Boolean(eventData?.business_name)
  );
  const hostPaidCancellationNotice = hostPaidDepositPayment || hostPaidRemainingPayment ? (
    <HostPaidCancellationNotice
      t={t}
      policy={hostPaidCancellationPolicy}
      hasProcessingFee={hasCheckoutProcessingFee}
    />
  ) : null;
  const requiresHostPaidParticipantConsent = Boolean(
    showPaymentUI && paymentContext?.isHostPaidCardEvent
  );
  const primaryTicketDisplayAmount = hostPaidRemainingPayment
    ? paymentTicketAmount
    : hasSplitTicketPayment
      ? depositAmount
      : formatMoney(
          Number.isFinite(effectiveFullTicketPrice) && effectiveFullTicketPrice > 0
            ? effectiveFullTicketPrice
            : effectiveCheckoutPrice
        );
  const fullTicketDisplayAmount = formatMoney(
    Number.isFinite(effectiveFullTicketPrice) && effectiveFullTicketPrice > 0
      ? effectiveFullTicketPrice
      : fallbackFullCheckoutAmount
  );
  const hostPaidOrganizerCard = requiresHostPaidParticipantConsent ? (
    <HostPaidOrganizerCard
      t={t}
      organizerName={organizerName}
      organizerType={organizerBusinessType}
    />
  ) : null;
  const hostPaidPayoutHoldNotice =
    hostPaidDepositPayment || hostPaidRemainingPayment ? (
      <div className="rounded-2xl border border-white/12 bg-white/[0.04] px-4 py-4 text-left text-sm leading-6 text-white/74">
        {String(paymentContext?.settlementMode || eventData?.settlement_mode || "").toLowerCase() === "direct"
          ? t(hostPaidRemainingPayment
              ? "confirm_intent_modal.body.host_paid_ticket_remaining_direct"
              : "confirm_intent_modal.body.host_paid_ticket_direct")
          : t(hostPaidRemainingPayment
              ? "confirm_intent_modal.body.host_paid_ticket_remaining_hold"
              : "confirm_intent_modal.body.host_paid_ticket_hold")}
      </div>
    ) : null;
  const hostPaidTermsConsentBlock = requiresHostPaidParticipantConsent ? (
    <label className="flex items-start gap-3 rounded-2xl border border-white/12 bg-white/[0.04] px-4 py-4 text-left">
      <input
        type="checkbox"
        checked={hostPaidParticipantTermsAccepted}
        onChange={(event) => setHostPaidParticipantTermsAccepted(event.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 rounded border-white/18 bg-transparent text-cyan-400 focus:ring-cyan-400"
      />
      <span className="text-sm leading-6 text-white/82">
        {t("confirm_intent_modal.body.terms_checkbox_prefix")}{" "}
        <a
          href={paidParticipantTermsHref}
          target="_blank"
          rel="noreferrer"
          className="text-cyan-300 underline underline-offset-4"
        >
          {t("confirm_intent_modal.body.terms_checkbox_terms_link")}
        </a>{" "}
        {t("confirm_intent_modal.body.terms_checkbox_suffix")}
      </span>
    </label>
  ) : null;

  const modalTitle = showAlreadyPaid
    ? t("confirm_intent_modal.header.title_ticket_registered")
    : isActivationDateVariant
    ? t(
        activationDateCountsAsIntent
          ? "confirm_intent_modal.header.title_confirm_event_date_intent"
          : isTimeOnlyScheduleChange
          ? "confirm_intent_modal.header.title_confirm_event_time"
          : "confirm_intent_modal.header.title_confirm_event_date"
      )
    : showPaymentUI
      ? hostPaidDepositPayment
        ? t("confirm_intent_modal.header.title_ticket")
        : hostPaidRemainingPayment
        ? t("confirm_intent_modal.header.title_remaining_balance")
        : preJoinPayment
        ? t("confirm_intent_modal.header.title_pay_to_join")
        : manualPaymentReminder
        ? t("confirm_intent_modal.header.title_get_ticket")
        : t("confirm_intent_modal.header.title_pay_to_keep")
      : t("confirm_intent_modal.header.title_confirm_intention");
  const hasScheduledDate = Boolean(eventData?.date_time) &&
    eventType !== "concept" && eventData?.type !== "concept";
  const getDeclineButtonLabel = (fallback) =>
    requiresDeclineClickConfirmation && declineConfirmArmed
      ? t("confirm_intent_modal.buttons.confirm_leave_event")
      : fallback;

  const modalFooter = blockedByNotParticipant ? (
    <AppModalButton
      onClick={onClose}
      disabled={loading || submitState === "pending"}
      loading={submitState === "pending"}
      className="text-[#2563EB]"
    >
      {t("confirm_intent_modal.buttons.ok")}
    </AppModalButton>
  ) : showAlreadyPaid ? (
    <AppModalButton
      onClick={onClose}
      disabled={loading || submitState === "pending"}
      loading={submitState === "pending"}
      className="text-[#2563EB]"
    >
      {t("confirm_intent_modal.buttons.ok")}
    </AppModalButton>
  ) : showPaymentUI ? (
    <>
      <AppModalButton
        onClick={() => handlePay(canChooseFullJoinPayment ? "join" : null)}
        disabled={
          loading ||
          submitState === "pending" ||
          redirecting ||
          multisportChoiceRequired ||
          ticketCategoryChoiceRequired ||
          (requiresHostPaidParticipantConsent && !hostPaidParticipantTermsAccepted)
        }
        loading={submitState === "pending" || redirecting}
        className="text-[#2563EB]"
      >
        {redirecting || submitState === "pending"
          ? t("confirm_intent_modal.buttons.pay_opening_checkout")
          : hostPaidDepositPayment && hasSplitTicketPayment
          ? paymentActionLabel(
              "confirm_intent_modal.buttons.join_with_deposit_price",
              primaryTicketDisplayAmount
            )
          : hostPaidDepositPayment
          ? paymentActionLabel(
              "confirm_intent_modal.buttons.pay_full_ticket_now_price",
              primaryTicketDisplayAmount
            )
          : hostPaidRemainingPayment
          ? paymentActionLabel(
              "confirm_intent_modal.buttons.pay_remaining_ticket_price",
              primaryTicketDisplayAmount
            )
          : effectiveCheckoutPrice
          ? paymentActionLabel(
              "confirm_intent_modal.buttons.pay_with_price",
              effectiveCheckoutPrice.toFixed(2)
            )
          : t("confirm_intent_modal.buttons.pay_now")}
      </AppModalButton>
      {canChooseFullJoinPayment ? (
        <AppModalButton
          variant="secondary"
          onClick={() => handlePay("join_full")}
          disabled={
            loading ||
            submitState === "pending" ||
            redirecting ||
            multisportChoiceRequired ||
            ticketCategoryChoiceRequired ||
            (requiresHostPaidParticipantConsent && !hostPaidParticipantTermsAccepted)
          }
          loading={submitState === "pending" || redirecting}
        >
          {redirecting || submitState === "pending"
            ? t("confirm_intent_modal.buttons.pay_opening_checkout")
            : paymentActionLabel(
                "confirm_intent_modal.buttons.pay_full_ticket_now_price",
                fullTicketDisplayAmount
              )}
        </AppModalButton>
      ) : null}
      {showQrOption ? (
        <AppModalButton
          variant="secondary"
          onClick={onShowQr}
          disabled={loading || submitState === "pending" || redirecting}
        >
          {t("confirm_intent_modal.buttons.show_qr")}
        </AppModalButton>
      ) : null}
    </>
  ) : (
    <>
      <AppModalButton
        onClick={handleConfirm}
        disabled={loading || submitState === "pending"}
        loading={submitState === "pending"}
        className={submitState === "success" ? "text-white" : "text-[#2563EB]"}
      >
        {{
          idle: isActivationDateVariant
            ? t(
                activationDateCountsAsIntent
                  ? "confirm_intent_modal.buttons.confirm_date_intent_idle"
                  : isTimeOnlyScheduleChange
                  ? "confirm_intent_modal.buttons.confirm_time_idle"
                  : "confirm_intent_modal.buttons.confirm_date_idle"
              )
            : t("confirm_intent_modal.buttons.confirm_idle"),
          pending: isActivationDateVariant
            ? t(
                activationDateCountsAsIntent
                  ? "confirm_intent_modal.buttons.confirm_date_intent_pending"
                  : isTimeOnlyScheduleChange
                  ? "confirm_intent_modal.buttons.confirm_time_pending"
                  : "confirm_intent_modal.buttons.confirm_date_pending"
              )
            : t("confirm_intent_modal.buttons.confirm_pending"),
          success: isActivationDateVariant
            ? t(
                activationDateCountsAsIntent
                  ? "confirm_intent_modal.buttons.confirm_date_intent_success"
                  : isTimeOnlyScheduleChange
                  ? "confirm_intent_modal.buttons.confirm_time_success"
                  : "confirm_intent_modal.buttons.confirm_date_success"
              )
            : t("confirm_intent_modal.buttons.confirm_success"),
          error: isActivationDateVariant
            ? t("confirm_intent_modal.buttons.confirm_date_error")
            : t("confirm_intent_modal.buttons.confirm_error"),
        }[submitState]}
      </AppModalButton>
      <AppModalButton
        variant="danger"
        onClick={handleDecline}
        disabled={loading || submitState === "pending"}
        loading={submitState === "pending"}
      >
        {submitState === "pending"
          ? t("confirm_intent_modal.buttons.processing")
          : getDeclineButtonLabel(
              isActivationDateVariant
                ? t(
                    paidRescheduleRefundAvailable
                      ? "confirm_intent_modal.buttons.decline_date_refund"
                      : activationDateCountsAsIntent
                      ? "confirm_intent_modal.buttons.decline_changed_plans"
                      : isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.buttons.decline_time_not_suitable"
                      : "confirm_intent_modal.buttons.decline_date_not_suitable"
                  )
                : t("confirm_intent_modal.buttons.decline_changed_plans")
            )}
      </AppModalButton>
    </>
  );

  if (!visible) return null;

  if (redirecting) {
    return (
      <AppModalShell
        open={visible}
        title={t("confirm_intent_modal.loader.title")}
        description={t("confirm_intent_modal.loader.subtitle")}
        closeAriaLabel={t("confirm_intent_modal.header.close_alt")}
        zIndexClassName="z-[13000]"
        maxWidthClassName="max-w-[30rem]"
        bodyClassName="px-6 py-4 text-center"
        descriptionClassName="text-center"
        rightAction={<div className="h-11 w-11" aria-hidden="true" />}
      >
        <div className="mx-auto my-3 w-full max-w-[15rem] space-y-3" aria-hidden="true">
          <LoadingSkeleton className="mx-auto h-4 w-[76%]" tone="strong" />
          <LoadingSkeleton className="mx-auto h-3 w-[54%]" />
        </div>
      </AppModalShell>
    );
  }

  return (
    <AppModalShell
      open={visible}
      title={modalTitle}
      onClose={onClose}
      closeAriaLabel={t("confirm_intent_modal.header.close_alt")}
      zIndexClassName="z-[13000]"
      maxWidthClassName="max-w-[30rem]"
      footer={<div className="grid grid-cols-1 gap-3">{modalFooter}</div>}
      bodyClassName="px-6 py-4"
    >
      <div className="space-y-3 text-center text-white/80">
        {blockedByNotParticipant ? (
          <p>{errorMsg || "You are not registered for this event."}</p>
        ) : showAlreadyPaid ? (
          <>
            <p>{t("confirm_intent_modal.body.ticket_already_registered")}</p>
          </>
        ) : eventData?.name ? (
          isActivationDateVariant ? (
            hasScheduledDate ? (
              <>
                <p>
                  {t(
                    activationDateCountsAsIntent
                      ? "confirm_intent_modal.body.activation_intent_with_name_and_date"
                      : isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.body.activation_time_with_name_and_date"
                      : "confirm_intent_modal.body.activation_with_name_and_date",
                    {
                      name: eventData.name,
                      date: formatEventDate(eventData.date_time),
                    }
                  )}
                </p>
                {paidRescheduleRefundAvailable ? (
                  <p>{t(
                    isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.body.activation_time_paid_refund_note"
                      : "confirm_intent_modal.body.activation_paid_refund_note"
                  )}</p>
                ) : null}
              </>
            ) : (
              <>
                <p>
                  {t(
                    activationDateCountsAsIntent
                      ? "confirm_intent_modal.body.activation_intent_with_name"
                      : isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.body.activation_time_with_name"
                      : "confirm_intent_modal.body.activation_with_name",
                    {
                      name: eventData.name,
                    }
                  )}
                </p>
                {paidRescheduleRefundAvailable ? (
                  <p>{t(
                    isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.body.activation_time_paid_refund_note"
                      : "confirm_intent_modal.body.activation_paid_refund_note"
                  )}</p>
                ) : null}
              </>
            )
          ) : (
          showPaymentUI ? (
            hostPaidDepositPayment ? (
              <>
                <p>
                  {t("confirm_intent_modal.body.host_paid_ticket_intro")}
                </p>
                {hostPaidOrganizerCard}
                {remainingPercent > 0 && Number.isFinite(balanceDueDaysBefore) ? (
                  <>
                    <p>
                      {t(balanceDueLabel ? "confirm_intent_modal.body.host_paid_ticket_deposit_split_deadline" : "confirm_intent_modal.body.host_paid_ticket_deposit_split", {
                        deadline: balanceDueLabel,
                        depositPercent,
                        depositAmount,
                        remainingPercent,
                        remainingAmount,
                        days: balanceDueDaysBefore,
                      })}
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      {t("confirm_intent_modal.body.host_paid_ticket_full_payment", {
                        amount: depositAmount,
                      })}
                    </p>
                  </>
                )}
                {canChooseFullJoinPayment ? (
                  <p>
                    {t("confirm_intent_modal.body.full_join_option_body", {
                      fullAmount: fullTicketDisplayAmount,
                    })}
                  </p>
                ) : null}
                {hostPaidCancellationNotice}
                {hostPaidPayoutHoldNotice}
                {hostPaidTermsConsentBlock}
              </>
            ) : hostPaidRemainingPayment ? (
              <>
                <p>{t("confirm_intent_modal.body.host_paid_ticket_intro")}</p>
                {hostPaidOrganizerCard}
                <p>
                  {t("confirm_intent_modal.body.host_paid_ticket_remaining_payment", {
                    remainingAmount,
                    remainingPercent,
                  })}
                </p>
                {hostPaidCancellationNotice}
                {hostPaidPayoutHoldNotice}
                {hostPaidTermsConsentBlock}
              </>
            ) : preJoinPayment ? (
              <>
                {isPlatformEvent && (
                  <p className="font-semibold text-white">
                    {t("confirm_intent_modal.body.payment_prejoin_platform_intro")}
                  </p>
                )}
                <p>
                  {t("confirm_intent_modal.body.payment_prejoin_line1")}
                </p>
                <p>
                  {t(
                    "confirm_intent_modal.body.payment_prejoin_line2_with_chat"
                  )}
                </p>
                <p>
                  {t("confirm_intent_modal.body.payment_prejoin_line3")}
                </p>
              </>
            ) : manualPaymentReminder ? (
              <>
                {isPlatformEvent ? (
                  <p className="font-semibold text-white">
                    {t("confirm_intent_modal.body.payment_prejoin_platform_intro")}
                  </p>
                ) : null}
                <p>
                  {t("confirm_intent_modal.body.payment_manual_line1")}
                </p>
                <p>
                  {t(
                    "confirm_intent_modal.body.payment_manual_line2_full"
                  )}
                </p>
                <p>
                  {t("confirm_intent_modal.body.payment_manual_line3")}
                </p>
              </>
            ) : (
              <>
                <p>
                  {t(
                    "confirm_intent_modal.body.payment_keep_spot_line1"
                  )}
                </p>
                <p>
                  {t(
                    "confirm_intent_modal.body.payment_keep_spot_line2"
                  )}
                </p>
                <p>
                  {t(
                    "confirm_intent_modal.body.payment_keep_spot_line3"
                  )}
                </p>
              </>
            )
          ) : hasScheduledDate ? (
            <p>
              {t("confirm_intent_modal.body.intent_with_name_and_date", {
                name: eventData.name,
                date: formatEventDate(eventData.date_time),
              })}
            </p>
          ) : (
            <p>
              {t("confirm_intent_modal.body.intent_with_name", {
                name: eventData.name,
              })}
            </p>
          )
          )
        ) : showPaymentUI ? (
          hostPaidDepositPayment ? (
            <>
              <p>{t("confirm_intent_modal.body.host_paid_ticket_intro")}</p>
              {hostPaidOrganizerCard}
              {remainingPercent > 0 && Number.isFinite(balanceDueDaysBefore) ? (
                <>
                  <p>
                    {t(balanceDueLabel ? "confirm_intent_modal.body.host_paid_ticket_deposit_split_deadline" : "confirm_intent_modal.body.host_paid_ticket_deposit_split", {
                        deadline: balanceDueLabel,
                      depositPercent,
                      depositAmount,
                      remainingPercent,
                      remainingAmount,
                      days: balanceDueDaysBefore,
                    })}
                  </p>
                </>
              ) : (
                <>
                  <p>
                    {t("confirm_intent_modal.body.host_paid_ticket_full_payment", {
                      amount: depositAmount,
                    })}
                  </p>
                </>
              )}
              {canChooseFullJoinPayment ? (
                <p>
                  {t("confirm_intent_modal.body.full_join_option_body", {
                    fullAmount: fullTicketDisplayAmount,
                  })}
                </p>
              ) : null}
              {hostPaidCancellationNotice}
              {hostPaidPayoutHoldNotice}
              {hostPaidTermsConsentBlock}
            </>
          ) : hostPaidRemainingPayment ? (
            <>
              <p>{t("confirm_intent_modal.body.host_paid_ticket_intro")}</p>
              {hostPaidOrganizerCard}
              <p>
                {t("confirm_intent_modal.body.host_paid_ticket_remaining_payment", {
                  remainingAmount,
                  remainingPercent,
                })}
              </p>
              {hostPaidCancellationNotice}
              {hostPaidPayoutHoldNotice}
              {hostPaidTermsConsentBlock}
            </>
          ) : preJoinPayment ? (
            <>
              {isPlatformEvent && (
                <p className="font-semibold text-white">
                  {t("confirm_intent_modal.body.payment_prejoin_platform_intro")}
                </p>
              )}
              <p>{t("confirm_intent_modal.body.payment_prejoin_line1")}</p>
              <p>
                {t(
                  "confirm_intent_modal.body.payment_prejoin_line2_short"
                )}
              </p>
            </>
          ) : manualPaymentReminder ? (
            <>
              {isPlatformEvent ? (
                <p className="font-semibold text-white">
                  {t("confirm_intent_modal.body.payment_prejoin_platform_intro")}
                </p>
              ) : null}
              <p>{t("confirm_intent_modal.body.payment_manual_line1")}</p>
              <p>
                {t(
                  "confirm_intent_modal.body.payment_manual_line2_short"
                )}
              </p>
            </>
          ) : (
            <>
              <p>
                {t(
                  "confirm_intent_modal.body.payment_generic_keep_spot_line1"
                )}
              </p>
              <p>
                {t(
                  "confirm_intent_modal.body.payment_generic_keep_spot_line2"
                )}
              </p>
            </>
          )
        ) : isActivationDateVariant ? (
          <>
            <p>
              {t(
                activationDateCountsAsIntent
                  ? "confirm_intent_modal.body.activation_intent_generic"
                  : isTimeOnlyScheduleChange
                  ? "confirm_intent_modal.body.activation_time_generic"
                  : "confirm_intent_modal.body.activation_generic"
              )}
            </p>
            {paidRescheduleRefundAvailable ? (
              <p>{t(
                isTimeOnlyScheduleChange
                  ? "confirm_intent_modal.body.activation_time_paid_refund_note"
                  : "confirm_intent_modal.body.activation_paid_refund_note"
              )}</p>
            ) : null}
          </>
        ) : (
          <p>{t("confirm_intent_modal.body.intent_generic")}</p>
        )}
      </div>

      {showPaymentUI && ticketCategoriesState?.enabled ? (
        <div className="mt-4 rounded-2xl border border-white/12 bg-white/[0.04] p-4 text-left">
          <div className="text-sm font-semibold uppercase tracking-[0.12em] text-white/55">
            {t("confirm_intent_modal.ticket_categories.title")}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2">
            {ticketCategoryOptions.map((item) => {
              const selected = String(item?.id || "") === String(selectedTicketCategory || "");
              return (
                <button
                  key={item.id}
                  type="button"
                  disabled={Boolean(ticketCategoriesState.locked)}
                  onClick={() => setSelectedTicketCategory(item.id)}
                  className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                    selected
                      ? "border-blue-500 bg-blue-600/10 text-white"
                      : "border-white/12 text-white/82"
                  } ${ticketCategoriesState.locked ? "cursor-default opacity-70" : ""}`}
                >
                  <span className="min-w-0 text-sm font-semibold">{item.name}</span>
                  <span className="shrink-0 text-sm text-white/68">
                    {formatMoney(item.price)} {paymentCurrencySymbol}
                  </span>
                </button>
              );
            })}
          </div>
          {ticketCategoriesState.locked ? (
            <p className="mt-3 text-sm text-white/62">
              {t("confirm_intent_modal.ticket_categories.locked")}
            </p>
          ) : null}
          {ticketCategoryChoiceRequired ? (
            <p className="mt-3 text-sm text-amber-200">
              {t("confirm_intent_modal.ticket_categories.choice_required")}
            </p>
          ) : null}
        </div>
      ) : null}

      {showPaymentUI && multisportPricing?.enabled ? (
        <div className="mt-4 rounded-2xl border border-white/12 bg-white/[0.04] p-4 text-left">
          <div className="text-sm font-semibold uppercase tracking-[0.12em] text-white/55">
            {t("confirm_intent_modal.multisport.title")}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2">
            <button
              type="button"
              disabled={Boolean(multisportPricing.locked)}
              onClick={() => setSelectedMultisport(false)}
              className={`rounded-2xl border px-4 py-3 text-left transition ${
                selectedMultisport === false
                  ? "border-blue-500 bg-blue-600/10 text-white"
                  : "border-white/12 text-white/82"
              } ${multisportPricing.locked ? "opacity-70 cursor-default" : ""}`}
            >
              <div className="text-sm font-semibold">
                {t("confirm_intent_modal.multisport.regular_label")}
              </div>
              <div className="mt-1 text-sm text-white/68">
                {t("confirm_intent_modal.multisport.regular_price", {
                  price: Number(multisportPricing.regular_ticket_price || 0).toFixed(2),
                }).replaceAll("€", paymentCurrencySymbol)}
              </div>
            </button>
            <button
              type="button"
              disabled={Boolean(multisportPricing.locked)}
              onClick={() => setSelectedMultisport(true)}
              className={`rounded-2xl border px-4 py-3 text-left transition ${
                selectedMultisport === true
                  ? "border-blue-500 bg-blue-600/10 text-white"
                  : "border-white/12 text-white/82"
              } ${multisportPricing.locked ? "opacity-70 cursor-default" : ""}`}
            >
              <div className="text-sm font-semibold">
                {t("confirm_intent_modal.multisport.multisport_label")}
              </div>
              <div className="mt-1 text-sm text-white/68">
                {t("confirm_intent_modal.multisport.multisport_price", {
                  price: Number(multisportPricing.multisport_ticket_price || 0).toFixed(2),
                }).replaceAll("€", paymentCurrencySymbol)}
              </div>
            </button>
          </div>
          {multisportPricing.locked ? (
            <p className="mt-3 text-sm text-white/62">
              {t("confirm_intent_modal.multisport.locked")}
            </p>
          ) : null}
          {multisportChoiceRequired ? (
            <p className="mt-3 text-sm text-amber-200">
              {t("confirm_intent_modal.multisport.choice_required")}
            </p>
          ) : null}
        </div>
      ) : null}

      {!blockedByNotParticipant && errorMsg && (
        <p className="mt-3 text-center text-sm text-red-400">
          {errorMsg}
        </p>
      )}
    </AppModalShell>
  );
};

export default ConfirmIntentModal;
